import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Upload, Pencil, Trash2 } from 'lucide-react';
import { useConfirmation } from '../shared/ZCard';
import OverflowMenu from '@/components/shared/OverflowMenu';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { SortablePhotoGrid } from './SortablePhotoGrid';
import { CollectionUpload } from './UploadDialogs';
import { CollectionForm } from './CollectionForm';

// ==================== COLLECTION DETAIL VIEW ====================
export function CollectionDetail({ collection, onBack, onRefresh, onViewPhoto, onTogglePublic, onPostcard, onEdit, onDelete }) {
  const feedback = useConfirmation();
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });
  const [photos, setPhotos] = useState(collection.photos || []);
  
  // Update local photos when collection changes
  useEffect(() => {
    setPhotos(collection.photos || []);
  }, [collection.photos]);

  const [isPublished, setIsPublished] = useState(collection.feeds_flow || false);

  const togglePublish = async () => {
    const newVal = !isPublished;
    setIsPublished(newVal);
    try {
      await api.put(`/framed/curated/${collection.id}`, { feeds_flow: newVal });
    } catch {
      setIsPublished(!newVal);
      feedback.fail();
    }
  };

  const confirmDeleteCollection = () => {
    setDeleteConfirm({ open: true, type: 'collection', id: collection.id, name: collection.title });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.type === 'collection') {
      try {
        await api.delete(`/framed/curated/${collection.id}`);
        feedback.confirm();
        onBack();
      } catch { feedback.fail(); }
    }
  };

  return (
    <div data-testid="collection-detail">
      {/* Collection Header with tagline */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4" style={{ position: 'relative' }}>
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>
            {collection.title}
          </h2>
          {collection.description && (
            <p className="italic mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0', fontSize: '17px' }}>
              {collection.description}
            </p>
          )}
          <p className="text-xs mt-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
            {photos.length} {photos.length === 1 ? 'photograph' : 'photographs'}
          </p>
        </div>
        <OverflowMenu
          publishToggle={{ isPublished, onToggle: togglePublish }}
          items={[
            { icon: <Upload size={16} strokeWidth={1.5} />, label: 'Upload photos', onClick: () => setShowUpload(true) },
            { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit info', onClick: () => setShowEdit(true) },
            { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete collection', onClick: confirmDeleteCollection, destructive: true },
          ]}
        />
      </div>

      {photos.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-sm mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
            This collection is empty.
          </p>
          <button onClick={() => setShowUpload(true)}
            className="text-xs tracking-[0.2em] uppercase px-4 py-2 border border-dashed border-white/[0.06] hover:border-[#6b7084] transition-colors rounded-full"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
            Upload First Photos
          </button>
        </div>
      ) : (
        /* Sortable Grid View */
        <SortablePhotoGrid
          photos={photos}
          onReorder={(newOrder) => {
            // Optimistic update - update UI immediately
            setPhotos(newOrder);
            // Save to backend in background
            api.put(`/framed/curated/${collection.id}`, { photo_ids: newOrder.map(p => p.id) })
              .then(() => feedback.confirm())
              .catch(() => {
                feedback.fail();
                onRefresh(); // Revert on error
              });
          }}
          onPhotoClick={onViewPhoto}
          collection={collection}
          showCoverBadge={true}
          onTogglePublic={onTogglePublic}
          onPostcard={onPostcard}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}

      <CollectionUpload open={showUpload} onClose={() => setShowUpload(false)} collectionId={collection.id} onSave={onRefresh} />
      <CollectionForm open={showEdit} onClose={() => setShowEdit(false)} onSave={onRefresh} collection={collection} />
      
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, type: '', id: null, name: '' })}
        onConfirm={handleDeleteConfirm}
        itemName={deleteConfirm.name}
        itemType={deleteConfirm.type}
      />
    </div>
  );
}
